/**
 * Mission draft serialization (issue #299, part of #24).
 *
 * Converts the interactive planner draft to/from the standard interchange
 * formats operators already use:
 *
 *  - MAVLink `.mission` — the QGC WPL 110 plain-text format written and read by
 *    QGroundControl, MAVProxy and Mission Planner. Each row encodes a MAVLink
 *    `MISSION_ITEM` (https://mavlink.io/en/messages/common.html#MISSION_ITEM).
 *  - KML — for sharing / viewing the route in Google Earth and GIS tools.
 *
 * Everything here is pure (no Leaflet, no Redux, no DOM) so it round-trips in a
 * unit test. The file download itself lives in utils/download.
 */
import { MissionDraft } from '../types/missionPlanner';
import { effectiveHomeBase } from './route';

// MAVLink constants (see mavlink.io common message set).
const MAV_FRAME_GLOBAL = 0; // absolute altitude — used for the home row
const MAV_FRAME_GLOBAL_RELATIVE_ALT = 3; // altitude relative to home
const MAV_CMD_NAV_WAYPOINT = 16;
const AUTOCONTINUE = 1;

/** A plain lat/lng, the only geometry the interchange formats carry. */
export interface LatLng {
  lat: number;
  lng: number;
}

/** The subset of a draft the standard formats can represent. */
export interface MissionIO {
  home: LatLng | null;
  waypoints: LatLng[];
  /** Target altitude (m, relative) written for every waypoint row. */
  altitudeM: number;
}

const QGC_HEADER = 'QGC WPL 110';

/** Build one QGC WPL 110 row (tab-separated MISSION_ITEM). */
const wplRow = (
  index: number,
  current: number,
  frame: number,
  { lat, lng }: LatLng,
  altM: number
): string =>
  [
    index,
    current,
    frame,
    MAV_CMD_NAV_WAYPOINT,
    0, // param1
    0, // param2
    0, // param3
    0, // param4
    lat.toFixed(8),
    lng.toFixed(8),
    altM.toFixed(6),
    AUTOCONTINUE,
  ].join('\t');

/**
 * Serialize a draft to QGC WPL 110 (`.mission`). Row 0 is the home point
 * (absolute frame), rows 1..N the waypoints (relative-altitude frame). The home
 * defaults to the mission start point when no custom home base is set.
 */
export const toMissionFile = (draft: MissionDraft): string => {
  const home = effectiveHomeBase(draft.waypoints, draft.homeBase);
  const altM = draft.profile.altitudeM;
  const rows: string[] = [QGC_HEADER];

  // Home is always row 0. Fall back to 0/0 only when there is no route at all.
  rows.push(wplRow(0, 1, MAV_FRAME_GLOBAL, home ?? { lat: 0, lng: 0 }, altM));

  draft.waypoints.forEach((wp, i) => {
    rows.push(
      wplRow(i + 1, 0, MAV_FRAME_GLOBAL_RELATIVE_ALT, { lat: wp.lat, lng: wp.lng }, altM)
    );
  });

  // Trailing newline — QGC and MAVProxy both expect a final line break.
  return `${rows.join('\n')}\n`;
};

/**
 * Parse a QGC WPL 110 (`.mission`) file back into home + waypoints. Throws a
 * loud, human error on a missing/unknown header (fail-safe: never silently
 * import garbage). Row 0 is treated as the home point; rows 1..N with valid
 * coordinates become waypoints, non-navigation `DO_*` rows (0/0 coords) skipped.
 */
export const fromMissionFile = (text: string): MissionIO => {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0 || !lines[0].startsWith(QGC_HEADER)) {
    throw new Error(
      `Unrecognised mission file — expected a "${QGC_HEADER}" header.`
    );
  }

  let home: LatLng | null = null;
  const waypoints: LatLng[] = [];
  let altitudeM = 0;

  for (const line of lines.slice(1)) {
    const cols = line.split(/\t+/);
    if (cols.length < 12) {
      throw new Error(
        `Malformed mission row (expected 12 columns): "${line}".`
      );
    }
    const seq = Number(cols[0]);
    const lat = Number(cols[8]);
    const lng = Number(cols[9]);
    const alt = Number(cols[10]);
    if (![seq, lat, lng, alt].every(Number.isFinite)) {
      throw new Error(`Malformed mission row (non-numeric field): "${line}".`);
    }

    const hasCoords = !(lat === 0 && lng === 0);
    if (seq === 0) {
      home = hasCoords ? { lat, lng } : null;
    } else if (hasCoords) {
      // Skip DO_* commands, which carry 0/0 coordinates.
      waypoints.push({ lat, lng });
      if (alt > 0) altitudeM = alt;
    }
  }

  return { home, waypoints, altitudeM };
};

/** XML-escape a KML text node / attribute value. */
const xmlEscape = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Serialize a draft to KML 2.2: a Placemark holding the route as a LineString,
 * plus one point Placemark per waypoint. KML coordinates are `lon,lat,alt`.
 */
export const toKml = (draft: MissionDraft, name = 'Mission'): string => {
  const altM = draft.profile.altitudeM;
  const coord = ({ lat, lng }: LatLng) => `${lng},${lat},${altM}`;
  const path = draft.waypoints.map((wp) => coord(wp)).join(' ');

  const placemarks = draft.waypoints
    .map(
      (wp) =>
        `    <Placemark>\n` +
        `      <name>WP ${wp.order}</name>\n` +
        `      <Point><coordinates>${coord(wp)}</coordinates></Point>\n` +
        `    </Placemark>`
    )
    .join('\n');

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<kml xmlns="http://www.opengis.net/kml/2.2">\n` +
    `  <Document>\n` +
    `    <name>${xmlEscape(name)}</name>\n` +
    `    <Placemark>\n` +
    `      <name>Route</name>\n` +
    `      <LineString><tessellate>1</tessellate>` +
    `<coordinates>${path}</coordinates></LineString>\n` +
    `    </Placemark>\n` +
    `${placemarks}\n` +
    `  </Document>\n` +
    `</kml>\n`
  );
};
